import React from 'react';
import { toast } from 'react-toastify';
import { useNavigate } from 'react-router-dom';
import { ArcSocketContext, ChannelResponseEnum } from '@src/core/socket';
import { CameraEntity, CommandStatusEnum, FlightEntity, FlightOrderDataEntity, FLightOrderStatusEnum, TelemetryAttiudeEulerEntity, TelemetryBatteryEntity, TelemetryDistanceSensorEntity, TelemetryEventEntity, TelemetryStatusTextEntity, TelemetryTypeEnum, TelemetryVelocityNedEntity, TelemetryVhrHudEntity } from '@src/features/order/entities';
import { IFlightContext } from "./types";
import { routesConfig } from '@src/core/routes';
import { TelemetryGpsInfoEntity } from '@src/features/order/entities/telemetry_gps_info';

export const FlightContext = React.createContext<IFlightContext>({
  flightData: {},
  cameraData: {},
});

export const FlightProvider: React.FC<React.PropsWithChildren> = ({ children }) => {

  const { subscribe, unsubscribe } = React.useContext(ArcSocketContext);

  const navigate = useNavigate();
  const [flightData, setFlightData] = React.useState<FlightEntity>({});
  const [cameraData, setCameraData] = React.useState<CameraEntity>({});

  const [flightMode, setFlightMode] = React.useState<{missionId: string, value: string}>();
  const [battery, setBattery] = React.useState<TelemetryBatteryEntity>();
  const [status, setStatus] = React.useState<TelemetryStatusTextEntity>();
  const [velocityNed, setVelocityNed] = React.useState<TelemetryVelocityNedEntity>();
  const [attitudeEuler, setAttitudeEuler] = React.useState<TelemetryAttiudeEulerEntity>();
  const [distanceSensor, setDistanceSensor] = React.useState<TelemetryDistanceSensorEntity>();
  const [gpsInfo, setGpsInfo] = React.useState<TelemetryGpsInfoEntity>();
  const [vhrNud, setVhrNud] = React.useState<TelemetryVhrHudEntity>();

  React.useEffect(() => {
    subscribe(ChannelResponseEnum.TELEMETRY, (event: TelemetryEventEntity) => {
      if (event.telemetry_type === TelemetryTypeEnum.FlightMode) {
        setFlightMode({missionId: event.currentMissionId, value: event.value as string});
        return;
      }
      if (event.telemetry_type === TelemetryTypeEnum.StatusText) {
        const value = event.value as { type: string; text: string };
        setStatus({
          text: value?.text || '',
          missionId: event.currentMissionId,
          type: value?.type || event.telemetry_type,
        } as TelemetryStatusTextEntity);
        return;
      }
      if (event.telemetry_type === TelemetryTypeEnum.Battery) {
        setBattery((event.value ?? {}) as TelemetryBatteryEntity);
        return;
      }
      if (event.telemetry_type === TelemetryTypeEnum.VelocityNed) {
        setVelocityNed((event.value ?? {}) as TelemetryVelocityNedEntity);
        return;
      }
      if (event.telemetry_type === TelemetryTypeEnum.AttitudeEuler) {
        setAttitudeEuler((event.value ?? {}) as TelemetryAttiudeEulerEntity);
        return;
      }
      if (event.telemetry_type === TelemetryTypeEnum.DistanceSensor) {
        setDistanceSensor((event.value ?? {}) as TelemetryDistanceSensorEntity);
        return;
      }
      if (event.telemetry_type === TelemetryTypeEnum.GpsInfo) {
        setGpsInfo((event.value ?? {}) as TelemetryGpsInfoEntity);
        return;
      }
      if(event.telemetry_type === TelemetryTypeEnum.VhrNud) {
        setVhrNud((event.value ?? {}) as TelemetryVhrHudEntity);
        return;
      }
    });
    subscribe(ChannelResponseEnum.FLIGHT_DATA, (event: { flightData: FlightEntity }) => {
      setFlightData(event.flightData ?? {});
    });
    subscribe(ChannelResponseEnum.CAMERA_DATA, (data: CameraEntity) => {
      setCameraData(data ?? {});
    });
    subscribe(ChannelResponseEnum.COMMAND_STATUS, (data) => {
      if (data.command === CommandStatusEnum.DELIVERY_COMPLETED) {
        if (data.status === 'ERROR') {
          toast.error('[DELIVERY_COMPLETED]: Something went wrong!');
          return;
        }
        toast.success(data.status ?? '');

      }
      if (data.command === CommandStatusEnum.START_MANUAL_CONTROL) {
        if (data.status === 'ERROR') {
          toast.error('[START_MANUAL_CONTROL]: Something went wrong!');
          return;
        }
        toast.success(data.status ?? '');
      }
      if (data.command === CommandStatusEnum.WINCH_DELIVERY) {
        if (data.status === 'ERROR') {
          toast.error('[WINCH_DELIVERY]: Something went wrong!');
          return;
        }
        toast.success(data.status ?? '');
      }
      if (data.command === CommandStatusEnum.WINCH_LOCK) {
        if (data.status === 'ERROR') {
          toast.error('[WINCH_LOCK]: Something went wrong!');
          return;
        }
        toast.success(data.status ?? '');
      }
      if (data.command === CommandStatusEnum.WINCH_UNLOCK) {
        if (data.status === 'ERROR') {
          toast.error('[WINCH_UNLOCK]: Something went wrong!');
          return;
        }
        toast.success(data.status ?? '');
      }
      if (data.command === CommandStatusEnum.WINCH_REEL_DOWN) {
        if (data.status === 'ERROR') {
          toast.error('[WINCH_REEL_DOWN]: Something went wrong!');
          return;
        }
        toast.success(data.status ?? '');
      }
      if (data.command === CommandStatusEnum.WINCH_REEL_UP) {
        if (data.status === 'ERROR') {
          toast.error('[WINCH_REEL_UP]: Something went wrong!');
          return;
        }
        toast.success(data.status ?? '');
      }
    });

    subscribe(ChannelResponseEnum.ORDER_STATUS, (event: { data: FlightOrderDataEntity }) => {
      if (event.data.status === FLightOrderStatusEnum.COMPLETED) {
        toast.success('Order Status Completed Text');
        navigate(routesConfig.orders);
        return;
      }
    });

    return () => {
      unsubscribe(ChannelResponseEnum.TELEMETRY);
      unsubscribe(ChannelResponseEnum.FLIGHT_DATA);
      unsubscribe(ChannelResponseEnum.CAMERA_DATA);
      unsubscribe(ChannelResponseEnum.ORDER_STATUS);
      unsubscribe(ChannelResponseEnum.COMMAND_STATUS);
    };
  }, [subscribe, unsubscribe]);


  return (
    <FlightContext.Provider 
      value={{ 
        status,
        battery, 
        flightData,
        velocityNed, 
        attitudeEuler, 
        distanceSensor,
        flightMode,
        cameraData ,
        gpsInfo,
        vhrNud, 
      }}>
      {children}
    </FlightContext.Provider>
  );
};